import { BadgeCheck, BedDouble, Check, ChevronDown, Globe, Heart, MapPin, Maximize2, Search, ShieldCheck, X } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useCompleteOAuthRedirect } from "../../../hooks/useAuth";

// Width-matched copies of the home hero (public/hero/), not the 1920px original.
const BishkekPhoto = "/hero/bishkek-1280.webp";
const BishkekPhotoSmall = "/hero/bishkek-800.webp";

const LANG_STORAGE_KEY = "app-language";

const LANGUAGES = [
  { code: "kg", label: "Кыргызча", flag: "🇰🇬" },
  { code: "kz", label: "Қазақша", flag: "🇰🇿" },
  { code: "uz", label: "O'zbekcha", flag: "🇺🇿" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
];

const BULLETS = [
  { key: "bullet1", Icon: BadgeCheck },
  { key: "bullet2", Icon: Search },
  { key: "bullet3", Icon: ShieldCheck },
] as const;

// Decorative price pins over the city photo — the page reads as a listings map.
const PINS = [
  { top: "22%", left: "16%", price: "$68 000", delay: "0s" },
  { top: "34%", left: "58%", price: "$124 500", delay: "1.2s", hot: true },
  { top: "14%", left: "72%", price: "$89 900", delay: "2.1s" },
  { top: "48%", left: "30%", price: "$54 000", delay: "0.6s" },
];

function AuthLanguageSwitcher({ tone = "light" }: { tone?: "light" | "glass" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const current =
    LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem(LANG_STORAGE_KEY, code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`h-9 flex items-center gap-1.5 pl-2.5 pr-2 rounded-full transition ${
          tone === "glass"
            ? "bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-md hover:bg-white/25"
            : "bg-gray-100 text-gray-600 hover:bg-gray-200/80 hover:text-gray-900"
        }`}
      >
        <Globe size={15} />
        <span className="text-xs font-semibold uppercase">{current.code}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="auth-pop absolute top-[calc(100%+8px)] left-0 min-w-[180px] bg-white rounded-2xl shadow-xl shadow-blue-900/10 ring-1 ring-gray-900/5 overflow-hidden z-50 p-1.5"
        >
          {LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-sm transition ${
                  active
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="text-base leading-none">{lang.flag}</span>
                <span className="flex-1">{lang.label}</span>
                {active && <Check size={15} className="text-blue-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CloseLink({ tone = "light" }: { tone?: "light" | "glass" }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/"
      aria-label={t("createListing.login.backHome", "На главную")}
      className={`h-9 w-9 flex items-center justify-center rounded-full transition ${
        tone === "glass"
          ? "bg-white/15 text-white ring-1 ring-white/25 backdrop-blur-md hover:bg-white/25"
          : "bg-gray-100 text-gray-500 hover:bg-gray-200/80 hover:text-gray-900"
      }`}
    >
      <X size={17} />
    </Link>
  );
}

function PricePin({ top, left, price, delay, hot, small }: (typeof PINS)[number] & { small?: boolean }) {
  return (
    <div className="auth-pin absolute" style={{ top, left, animationDelay: delay }} aria-hidden>
      <div
        className={`relative flex items-center gap-1.5 rounded-full font-bold shadow-lg ${
          small ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-[13px]"
        } ${hot ? "bg-blue-600 text-white shadow-blue-900/40" : "bg-white text-gray-900 shadow-black/25"}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${hot ? "bg-white" : "bg-blue-600"}`} />
        {price}
        <span
          className={`absolute left-1/2 top-full -translate-x-1/2 border-x-[6px] border-t-[7px] border-x-transparent ${
            hot ? "border-t-blue-600" : "border-t-white"
          }`}
        />
      </div>
    </div>
  );
}

/** Mock listing card floating over the photo (desktop visual only). */
function ListingCard() {
  const { t } = useTranslation();
  return (
    <div
      aria-hidden
      className="auth-float-card w-[250px] rounded-2xl bg-white/95 p-2.5 text-gray-900 shadow-2xl shadow-black/30 ring-1 ring-white/60 backdrop-blur"
    >
      <div
        className="relative h-[104px] rounded-xl bg-cover"
        style={{ backgroundImage: `url(${BishkekPhotoSmall})`, backgroundPosition: "65% 70%" }}
      >
        <span className="absolute left-2 top-2 rounded-md bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          New
        </span>
        <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-rose-500">
          <Heart size={14} fill="currentColor" />
        </span>
      </div>
      <div className="px-1 pb-0.5 pt-2.5">
        <p className="text-lg font-extrabold tracking-tight">$124 500</p>
        <div className="mt-1 flex items-center gap-3 text-[12px] font-medium text-gray-500">
          <span className="flex items-center gap-1"><BedDouble size={13} /> 3</span>
          <span className="flex items-center gap-1"><Maximize2 size={12} /> 96 m²</span>
          <span className="flex min-w-0 items-center gap-1 truncate">
            <MapPin size={12} className="shrink-0" /> {t("auth.illustration.city", "Бишкек")}
          </span>
        </div>
      </div>
    </div>
  );
}

interface AuthShellProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  /** Small icon badge above the title (secondary flows). */
  icon?: ReactNode;
  /** Wider form column (two-column Register on desktop). */
  wide?: boolean;
}

/**
 * Auth layout, built to fit one screen without scrolling:
 * – lg+: full-height split — city photo with price pins, a listing card
 *   and the selling points on the left, form on the right;
 * – below: photo header with the topbar, the form as a sheet over it
 *   (full width on phones, a centred card on tablets).
 * Very short screens still scroll rather than clip.
 */
export function AuthShell({ title, subtitle, children, icon, wide }: AuthShellProps) {
  const { t } = useTranslation();

  // Finish a redirect-based Google/Apple sign-in when the browser returns
  // here (web uses a full-page redirect instead of a popup).
  useCompleteOAuthRedirect();

  const heading = (
    <div className="mb-4 auth-short:mb-3 auth-tall:mb-5">
      {icon && (
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 text-blue-600 ring-1 ring-blue-100">
          {icon}
        </div>
      )}
      <h1 className="text-[22px] sm:text-[28px] font-extrabold text-gray-900 tracking-tight leading-tight">
        {title}
      </h1>
      <p className="mt-1 text-sm sm:text-[15px] text-gray-500 auth-short:hidden">{subtitle}</p>
    </div>
  );

  return (
    <div className="relative min-h-[100dvh] bg-[#0b1640] lg:bg-white lg:grid lg:h-[100dvh] lg:grid-cols-[minmax(0,1.15fr)_minmax(500px,1fr)] lg:overflow-hidden">
      {/* ───── Photo: page background below lg ───── */}
      <div className="lg:hidden fixed inset-0" aria-hidden>
        <img src={BishkekPhotoSmall} alt="" decoding="async" className="auth-kenburns h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1640]/55 via-[#0b1640]/75 to-[#0b1640]" />
      </div>

      {/* ───── Form column ───── */}
      <div className="relative z-10 flex min-h-[100dvh] flex-col lg:min-h-0 lg:h-full lg:overflow-y-auto">
        {/* Topbar: glass over the photo below lg, plain on the white desktop column */}
        <header className="flex items-center justify-between gap-3 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 auth-tiny:pt-[max(0.5rem,env(safe-area-inset-top))] auth-tiny:pb-2 sm:px-6 lg:px-10 lg:pt-6">
          <div className="lg:hidden">
            <AuthLanguageSwitcher tone="glass" />
          </div>
          <div className="hidden lg:block">
            <AuthLanguageSwitcher />
          </div>
          <Link to="/" className="text-lg font-extrabold tracking-tight text-white lg:text-blue-600 lg:text-xl">
            TURAQ.KG
          </Link>
          <div className="lg:hidden">
            <CloseLink tone="glass" />
          </div>
          <div className="hidden lg:block">
            <CloseLink />
          </div>
        </header>

        {/* Price pins peeking above the sheet — only where height allows */}
        <div className="lg:hidden" aria-hidden>
          <div className="relative hidden h-24 auth-roomy:block">
            <PricePin {...PINS[0]} top="18%" left="8%" small />
            <PricePin {...PINS[1]} top="38%" left="46%" small />
            <PricePin {...PINS[2]} top="6%" left="74%" small />
          </div>
        </div>

        <main className="flex flex-1 flex-col sm:items-center sm:justify-center sm:px-6 sm:pb-8 lg:px-10 lg:pb-6">
          <div
            className={`auth-sheet-enter flex w-full flex-1 flex-col rounded-t-[28px] bg-white px-5 pt-5 auth-tiny:pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.45)] sm:flex-none sm:rounded-[28px] sm:px-9 sm:py-8 sm:shadow-2xl lg:rounded-none lg:p-0 lg:shadow-none ${
              wide ? "sm:max-w-[560px] lg:max-w-[520px]" : "sm:max-w-[460px] lg:max-w-[420px]"
            }`}
          >
            {heading}
            {children}
          </div>
        </main>
      </div>

      {/* ───── Visual panel (lg+) ───── */}
      <aside className="relative hidden lg:block lg:order-first p-3 pr-0" aria-hidden>
        <div className="relative h-full overflow-hidden rounded-[28px] text-white">
          <img src={BishkekPhoto} alt="" decoding="async" className="auth-kenburns absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b1640] via-[#13245e]/45 to-[#13245e]/10" />

          {PINS.map((p) => (
            <PricePin key={p.price} {...p} />
          ))}

          <div className="absolute right-8 top-[46%] xl:right-12">
            <ListingCard />
          </div>

          <div className="absolute inset-x-0 bottom-0 p-10 xl:p-12">
            <span className="inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md ring-1 ring-white/25 text-[11px] font-semibold uppercase tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px] shadow-emerald-400" />
              {t("auth.illustration.badge", "Turaq.kg")}
            </span>
            <h2 className="mt-4 max-w-md text-[32px] xl:text-[40px] font-extrabold leading-[1.08] tracking-tight text-balance">
              {t("auth.illustration.heading", "Найдите свой дом мечты")}
            </h2>
            <p className="mt-3 max-w-md text-[15px] leading-relaxed text-white/80">
              {t(
                "auth.illustration.subheading",
                "Тысячи объявлений по всему Кыргызстану — в одном месте.",
              )}
            </p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {BULLETS.map(({ key, Icon }) => (
                <li
                  key={key}
                  className="flex items-center gap-2 rounded-full bg-white/12 backdrop-blur-md ring-1 ring-white/20 py-1.5 pl-1.5 pr-3.5 text-[13px] font-medium"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                    <Icon size={13} className="text-sky-100" />
                  </span>
                  {t(`auth.illustration.${key}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}
